import { useEffect, useRef, useState } from "react";

import { ArrowClockwise } from "@phosphor-icons/react";

type MapLoadingPillProps = {
  show: boolean;
  label: string;
  className?: string;
};

/**
 * Small loading pill that sits atop the map without intercepting pointer events.
 */
export function MapLoadingPill({ show, label, className }: MapLoadingPillProps) {
  const [render, setRender] = useState(show);
  const [visible, setVisible] = useState(show);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minVisibleMs = 600;
  const fadeMs = 220;
  const lastShownAt = useRef<number>(show ? Date.now() : 0);

  useEffect(() => {
    if (show) {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      lastShownAt.current = Date.now();
      setRender(true);
      requestAnimationFrame(() => setVisible(true));
      return;
    }

    const elapsed = Date.now() - lastShownAt.current;
    const delay = Math.max(minVisibleMs - elapsed, fadeMs);
    hideTimer.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => setRender(false), fadeMs);
    }, delay);

    return () => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    };
  }, [show]);

  if (!render) return null;

  return (
    <div
      className={[
        "pointer-events-none absolute bottom-4 left-4 flex items-center gap-3 rounded-full bg-slate-900/80 px-3 py-2 text-xs text-white shadow-lg shadow-slate-900/40 backdrop-blur-sm transition-opacity duration-300 ease-in-out dark:bg-slate-900/90",
        visible ? "opacity-100" : "opacity-0",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-live="polite"
    >
      <ArrowClockwise
        className="h-4 w-4 animate-spin text-white drop-shadow motion-reduce:animate-none"
        aria-hidden
      />
      <span className="font-semibold">{label}</span>
    </div>
  );
}
